/**
 * constant.js
 *
 * This file includes all application-wide constant values. This may include
 * enums, reference IDs or data. For example:
 *
 * - data like the list of emails to show in various dropdowns
 * - account IDs for enabling specific functionality
 *
 * Specifically in __THIS__ file are constants specific to the Console
 * application UI. All API-related constants *should* be in in
 * `adready-api/constant`.
 */
import {
  LIS_DRAFT,
  LIS_NEW,
  LIS_PENDING_ACTIVATION,
  LIS_ACTIVATED,
  LIS_DISAPPROVED,
  LIS_CAMPAIGN_LIVE,
  LIS_PAUSED,
  LIS_ENDED,
  LIS_CANCELED,
  LIS_REOPENED,
  LIS_PENDING_CANCELLATION,
  LIS_PENDING_PAUSED,
  IOS_DRAFT,
  IOS_NEW,
  IOS_COMPLETED,
  IOS_LIVE,
  LIS_CHANGE_NEW,
  LIS_CHANGE_PENDING_ACTIVATION,
} from 'adready-api/constant';

export const DISPLAY_STATUS_NAME_DRAFT = 'Draft';
export const DISPLAY_STATUS_NAME_SUBMITTED = 'Submitted';
export const DISPLAY_STATUS_NAME_LIVE = 'Live';
export const DISPLAY_STATUS_NAME_PAUSED = 'Paused';
export const DISPLAY_STATUS_NAME_ENDED = 'Ended';
export const DISPLAY_STATUS_NAME_CANCELED = 'Canceled';
export const ENABLE_SEGMENT_TOOL = true;

export const FLIP_FILTER_CACHE_KEY = 'filters_cache';
export const debug = false; // Change this to false to disable logs

export const DISPLAY_STATUS_NAMES_MAP = {
  [IOS_DRAFT]: DISPLAY_STATUS_NAME_DRAFT,
  [IOS_NEW]: DISPLAY_STATUS_NAME_SUBMITTED,
  [IOS_LIVE]: DISPLAY_STATUS_NAME_LIVE,
  [IOS_COMPLETED]: DISPLAY_STATUS_NAME_ENDED,
  [LIS_DRAFT]: DISPLAY_STATUS_NAME_DRAFT,
  [LIS_NEW]: DISPLAY_STATUS_NAME_SUBMITTED,
  [LIS_PENDING_ACTIVATION]: DISPLAY_STATUS_NAME_SUBMITTED,
  [LIS_ACTIVATED]: DISPLAY_STATUS_NAME_SUBMITTED,
  [LIS_DISAPPROVED]: DISPLAY_STATUS_NAME_SUBMITTED,
  [LIS_REOPENED]: DISPLAY_STATUS_NAME_SUBMITTED,
  [LIS_CAMPAIGN_LIVE]: DISPLAY_STATUS_NAME_LIVE,
  [LIS_PAUSED]: DISPLAY_STATUS_NAME_PAUSED,
  [LIS_ENDED]: DISPLAY_STATUS_NAME_ENDED,
  [LIS_CANCELED]: DISPLAY_STATUS_NAME_CANCELED,
  [LIS_PENDING_CANCELLATION]: DISPLAY_STATUS_NAME_CANCELED,
  [LIS_PENDING_PAUSED]: DISPLAY_STATUS_NAME_PAUSED,
};

// localStorage key name for accountId
export const KEY_ACCOUNT_ID = 'accountId';
export const KEY_ADVERTISER_ID = 'advertiserId';
export const DEMO_DOMAIN_PART = 'demo';
export const KEY_DEMO_ACCOUNT_ID = 'demoSelectedAccountId';
export const KEY_DEMO_ADVERTISER_ID = 'demoSelectedAdvertiserId';

export const VISITS = 'visits';
export const CURRENCY = 'CURRENCY';
export const PERCENTAGE = 'PERCENTAGE';
export const DECIMAL_PERCENTAGE = 'DECIMAL_PERCENTAGE';
export const FREQUENCY = 'FREQUENCY';
export const DECIMAL = 'DECIMAL';

export const SCHEDULE_FREQUENCY_OPTIONS = [
  {
    id: 'select',
    name: 'Select Frequency',
  },
  {
    id: 'WEEKLY',
    name: 'Weekly',
  },
  {
    id: 'BI_WEEKLY',
    name: 'Bi-Weekly',
  },
  {
    id: 'MONTHLY',
    name: 'Monthly',
  },
  {
    id: 'MONTH_END',
    name: 'End of Month',
  },
];

export const DEFAULT_BRAND_COLOR = '#006987';
export const AUDIENCE_INSIGHTS_REPORT_TEMPLATE_EXCLUDED_ACCOUNTS = [
  2,
  125,
  191,
  504,
  548,
  630,
  409,
  637,
  253,
  549,
  233,
  70,
  274,
  609,
  639,
  338,
  386,
  339,
];

export const COLOR_PATTERN = [
  '#00abb7',
  '#5c80bc',
  '#4d5061',
  '#4e4b45',
  '#e7e247',
  '#ed474a',
  '#af5d63',
  '#846075',
  '#4a6c6f',
  '#503d3f',
  '#e9edde',
  '#e23d56',
  '#ffd151',
  '#ff8f3a',
  '#b7c776',
  '#716cde',
  '#775ec7',
  '#e85d52',
  '#ff6843',
  '#ffaa43',
  '#ffdd53',
  '#59c16a',
  '#42ad8f',
  '#23c5da',
  '#559595',
  '#528d9d',
  '#3d6c8a',
  '#415e85',
  '#415e85',
  '#544a74',
  '#564b77',
  '#58436a',
  '#664669',
  '#894968',
  '#d94d60',
];

export const DATE_FORMAT_MM_DD_YY = 'MM/DD/YY';
export const DEFAULT_DR_LOGO = '/assets/default/dr-logo-400x140.png';
export const SQUARE_DR_LOGO = '/assets/default/logos_dr-icon-90x90.png';
export const APP_ADREADY_ID = 1;
export const APP_FLIP_ID = 4;
export const APP_CONSOLE_ID = 5;
export const DEMO_APP_CONSOLE_ID = 6;
export const SEGMENT_SEARCH_ID = 7;
export const REPORT_BUILDER_2 = 8;
export const AI_ASSISTANT_REPORT_BUILDER = 9;
export const AI_ASSISTANT_FULL_PLATFORM = 10;
export const EXCLUDED_APPS = [2, 3, 4];
export const INTERNAL_ADMIN_ROLE_ID = 9;
export const INTERNAL_SUB_ADMIN_ROLE_ID = 10;
export const INTERNAL_USER_ROLE_ID = 11;
export const ALL_INTERNAL_USER_ROLE_ID = [
  INTERNAL_ADMIN_ROLE_ID,
  INTERNAL_SUB_ADMIN_ROLE_ID,
  INTERNAL_USER_ROLE_ID,
];
export const EXTERNAL_ROLES_DEFAULT_APPS = [
  APP_CONSOLE_ID,
  AI_ASSISTANT_FULL_PLATFORM,
  REPORT_BUILDER_2,
];
export const REPORT_BUILDER_2_LABEL = 'Report Builder 2.0';
export const REPORT_BUILDER_2_LABEL_AP = 'Report Builder 2.0 AP';
export const PIXEL_LINK = 'Pixel';

export const AI_ASSISTANT_PLATFORM_URL =
  'https://demo.digitalremedy.com/ai/assistant/v2/inject-assistant.js';
export const AI_ASSISTANT_REPORT_BUILDER_URL =
  'https://demo.digitalremedy.com/ai/assistant/v2/inject-assistant.js';
export const AI_ASSISTANT_MNI_LOGO_URL =
  'https://demo.digitalremedy.com/ai/assistant/v2/mni-logo.js';

export const ADVANCED_PERFORMANCE_SOLUTION_ID = 1;
export const PROGRAMMATIC_MEDIA_SOLUTION_ID = 2;
export const SEARCH_SOLUTION_ID = 3;
export const META_SOLUTION_ID = 4;
export const DOOH_SOLUTION_ID = 5;
export const YOUTUBE_SOLUTION_ID = 6;
export const CONVERGED_TV_SOLUTION_ID = 7;
export const MNI_MOTTO_SOLUTION_ID = 8;

export const RANGE_CUSTOM = 0;
export const RANGE_LAST_30_DAYS = 7;
export const RANGE_CAMPAIGN_TO_DATE = 13;
export const COMPARE_RANGE_PREV_DAY = 4;
export const COMPARE_RANGE_CUSTOM = 0;

export const CAMPAIGN_FILTER = 'campaign_filter';
export const ADGROUP_FILTER = 'adgroup_filter';
export const PIXEL_FILTER = 'pixel_filter';
export const DATE_FILTER = 'date_filter';
export const ADVANCED_FILTER = 'advanced_filter';
export const MEDIATYPE_FILTER = 'mediatype_filter';
export const CREATIVE_FILTER = 'creative_filter';
export const AUDIENCE_FILTER = 'audience_filter';
export const AD_FILTER = 'ad_filter';
export const KEYWORD_FILTER = 'keyword_filter';
export const CONVERSION_WINDOW_FILTER = 'conversion_window_filter';
export const MY_REPORTS = 'my_reports';
export const AGENCY_REPORTS = 'agency_reports';
export const SEARCH_DATA = 'search_data';
export const SEGMENTS_SEARCH = 'segments_search';
export const GOOGLE_ANALYTICS = 'google_analytics';
export const REPORT_BUILDER_2_NAME = 'report_builder_2';
export const PIXEL_LINK_NAME = 'pixel_link';
export const MAX_SUB_CATEGORIES_COUNT_ALLOWED = 50;
export const MAX_DAYS_TO_CONVERT = [
  { value: '1', checked: false },
  { value: '2', checked: false },
  { value: '3', checked: false },
  { value: '4', checked: false },
  { value: '5', checked: false },
  { value: '6', checked: false },
  { value: '7', checked: false },
  { value: '8', checked: false },
  { value: '9', checked: false },
  { value: '10', checked: false },
  { value: '11', checked: false },
  { value: '12', checked: false },
  { value: '13', checked: false },
  { value: '14', checked: false },
  { value: '15', checked: false },
  { value: '16', checked: false },
  { value: '17', checked: false },
  { value: '18', checked: false },
  { value: '19', checked: false },
  { value: '20', checked: false },
  { value: '21', checked: false },
  { value: '22', checked: false },
  { value: '23', checked: false },
  { value: '24', checked: false },
  { value: '25', checked: false },
  { value: '26', checked: false },
  { value: '27', checked: false },
  { value: '28', checked: false },
  { value: '29', checked: false },
  { value: '30', checked: false },
  { value: '31', checked: true },
];
export const MAX_DAYS_TO_CONVERT_JOURNEY = [
  { value: '1', checked: false },
  { value: '2', checked: false },
  { value: '3', checked: false },
  { value: '4', checked: false },
  { value: '5', checked: false },
  { value: '6', checked: false },
  { value: '7', checked: false },
  { value: '8', checked: false },
  { value: '9', checked: false },
  { value: '10', checked: false },
  { value: '11', checked: false },
  { value: '12', checked: false },
  { value: '13', checked: false },
  { value: '14', checked: false },
  { value: '15', checked: false },
  { value: '16', checked: false },
  { value: '17', checked: false },
  { value: '18', checked: false },
  { value: '19', checked: false },
  { value: '20', checked: false },
  { value: '21', checked: false },
  { value: '22', checked: false },
  { value: '23', checked: false },
  { value: '24', checked: false },
  { value: '25', checked: false },
  { value: '26', checked: false },
  { value: '27', checked: false },
  { value: '28', checked: false },
  { value: '29', checked: false },
  { value: '30', checked: false },
  { value: '31', checked: false },
  { value: '32', checked: false },
  { value: '33', checked: false },
  { value: '34', checked: false },
  { value: '35', checked: false },
  { value: '36', checked: false },
  { value: '37', checked: false },
  { value: '38', checked: false },
  { value: '39', checked: false },
  { value: '40', checked: false },
  { value: '41', checked: false },
  { value: '42', checked: false },
  { value: '43', checked: false },
  { value: '44', checked: false },
  { value: '45', checked: false },
  { value: '46', checked: false },
  { value: '47', checked: false },
  { value: '48', checked: false },
  { value: '49', checked: false },
  { value: '50', checked: false },
  { value: '51', checked: false },
  { value: '52', checked: false },
  { value: '53', checked: false },
  { value: '54', checked: false },
  { value: '55', checked: false },
  { value: '56', checked: false },
  { value: '57', checked: false },
  { value: '58', checked: false },
  { value: '59', checked: false },
  { value: '60', checked: false },
  { value: '61', checked: false },
  { value: '62', checked: false },
  { value: '63', checked: false },
  { value: '64', checked: false },
  { value: '65', checked: false },
  { value: '66', checked: false },
  { value: '67', checked: false },
  { value: '68', checked: false },
  { value: '69', checked: false },
  { value: '70', checked: false },
  { value: '71', checked: false },
  { value: '72', checked: false },
  { value: '73', checked: false },
  { value: '74', checked: false },
  { value: '75', checked: false },
  { value: '76', checked: false },
  { value: '77', checked: false },
  { value: '78', checked: false },
  { value: '79', checked: false },
  { value: '80', checked: false },
  { value: '81', checked: false },
  { value: '82', checked: false },
  { value: '83', checked: false },
  { value: '84', checked: false },
  { value: '85', checked: false },
  { value: '86', checked: false },
  { value: '87', checked: false },
  { value: '88', checked: false },
  { value: '89', checked: false },
  { value: '90', checked: true },
];

export const DEFAULT_CONVERSION_WINDOW = 31;
export const CONVERSION_TYPES = [
  { key: 'imp', value: 'imp', name: 'Views', selected: true },
  { key: 'click', value: 'click', name: 'Clicks', selected: true },
];

export const DEFAULT_MNI_ADVERTISER_TEMPLATE_MAPPING = 3;

export const DYNAMIC_PIXEL_CONVERSION_COLUMNS = [
  {
    key: 'pixel1_conversions',
    value: 'pixelName',
    align: 'right',
    formatter: { type: 'number', format: '0,0' },
  },
  {
    key: 'pixel1_cpa',
    infinityKey: 'pixel1_cpaInfinity',
    value: 'pixelName CPA',
    align: 'right',
    formatter: { type: 'number', format: '$0.[00]' },
  },
  {
    key: 'pixel1_revenue',
    value: 'pixelName Revenue',
    align: 'right',
    formatter: { type: 'number', format: '$0.[00]' },
  },
  {
    key: 'pixel1_roas',
    infinityKey: 'pixel1_roasInfinity',
    value: 'pixelName ROAS',
    align: 'right',
    formatter: { type: 'percentage', format: '0.[0]%' },
  },
];

export const DATEPICKER_RANGE_OPTIONS = {
  '0': 'Custom',
  '1': 'Today',
  '2': 'Yesterday',
  '3': 'Last Week',
  '5': 'Last Month',
  '6': 'Last 7 Days',
  '7': 'Last 30 Days',
  '8': 'Last 60 Days',
  '9': 'Last 90 Days',
  '13': 'Campaign To Date',
  '14': 'Last 2 Weeks',
};
export const DATEPICKER_COMPARE_RANGE_OPTIONS = {
  '0': 'Custom',
  '1': 'Previous Period',
  '2': 'Previous Year',
  '3': 'Previous Month',
  '4': 'Previous Day',
};

// List of account ids for whom to hide inactive solutions
// Account Id 70 = VI Marketing & Branding
// Account Id 77 = Noble People
// Account Id 81 = Tinuiti
// Account Id 114 = Network Affiliates
// Account Id 125 = Ron Foth
// Account Id 182 = Veritas Media Group
// Account Id 191 = Compulse
// Account Id 202 = The Gate Worldwide
// Account Id 208 = Media.Monks
// Account Id 274 = Motivas
// Account Id 315 = Giant Spoon
// Account Id 334 = Media Brokers International
export const ACCOUNTS_TO_HIDE_INACTIVE_SOLUTIONS = [
  70,
  77,
  81,
  114,
  125,
  182,
  191,
  202,
  208,
  274,
  315,
  334,
  404,
  609,
];

export const SHOW_ONLY_SOLUTIONS_BY_ACCOUNT = [
  {
    accountId: 404, // acme
    isDemoAccount: true,
    showOnlySolutions: ['Advanced Performance'],
  },
];

// Account Id constants
export const ROUX_ADVERTISING_ID = 609;

export const MNI_MOTTO_CUSTOM_REPORT_TITLE = 'Media Now Interactive // Custom Reports';

export const MNI_ACCOUNT_ID = 76; // mni motto - ancora education

export const MNI_DEMO_ACCOUNT_ID = 572; // mni motto demo account
export const DSP = [
  { id: 'XANDR', name: 'Xandr' },
  { id: 'YAHOO', name: 'Yahoo' },
];
export const VERTICALS = [
  { id: 'Arts & Entertainment', name: 'Arts & Entertainment' },
  { id: 'Automotive', name: 'Automotive' },
  { id: 'Business', name: 'Business' },
  { id: 'Careers', name: 'Careers' },
  { id: 'Education', name: 'Education' },
  { id: 'Family & Parenting', name: 'Family & Parenting' },
  { id: 'Health & Fitness', name: 'Health & Fitness' },
  { id: 'Food & Drink', name: 'Food & Drink' },
  { id: 'Hobbies & Interests', name: 'Hobbies & Interests' },
  { id: 'Home & Garden', name: 'Home & Garden' },
  { id: 'Law, Government & Politics', name: 'Law, Government & Politics' },
  { id: 'News', name: 'News' },
  { id: 'Personal Finance', name: 'Personal Finance' },
  { id: 'Society', name: 'Society' },
  { id: 'Science', name: 'Science' },
  { id: 'Pets', name: 'Pets' },
  { id: 'Sports', name: 'Sports' },
  { id: 'Style & Fashion', name: 'Style & Fashion' },
  { id: 'Technology & Computing', name: 'Technology & Computing' },
  { id: 'Travel', name: 'Travel' },
  { id: 'Real Estate', name: 'Real Estate' },
  { id: 'Shopping', name: 'Shopping' },
  { id: 'Religion & Spirituality', name: 'Religion & Spirituality' },
  { id: 'Uncategorized', name: 'Uncategorized' },
  { id: 'Non-Standard Content', name: 'Non-Standard Content' },
  { id: 'Illegal Content', name: 'Illegal Content' },
];
export const VIA_ACCOUNT_ID = 73; // VIA Media 2021

export const MNI_REPORTING_TABLEAU_URL = '/views/MNIGenericTemplate';

export const REACH_AND_FREQ_DASHBOARD = {
  title: 'Reach and Frequency',
  icon: 'chart-simple',
  handler: 'EmbeddedDashboard',
  embedUrlType: 'reach_and_frequency',
  order: 2,
};

export const REACH_FREQ_DASHBOARD_FEATURE = 'R&F Dashboard';

export const REACH_AND_FREQUENCY_TAB_OPTIONS = [
  {
    id: 'reachAndFrequencyOverview',
    handler: 'tableauReportHandler',
    value: 'R & F Overview',
    viewUrl: '/views/EnhancedReachAndFrequencyReporting/Overview',
    demoViewUrl: '/views/DemoEnhancedReachAndFrequencyReporting/Overview',
  },
  {
    id: 'reachAndFrequencyPublisherInsights',
    handler: 'tableauReportHandler',
    value: 'R & F Publisher Insights',
    viewUrl: '/views/EnhancedReachAndFrequencyReporting/PublishersInsights',
    demoViewUrl: '/views/DemoEnhancedReachAndFrequencyReporting/PublishersInsights',
  },
  {
    id: 'reachAndFrequencyAudienceInsights',
    handler: 'tableauReportHandler',
    value: 'R & F Audience Insights',
    viewUrl: '/views/EnhancedReachAndFrequencyReporting/AudienceInsights',
    demoViewUrl: '/views/DemoEnhancedReachAndFrequencyReporting/AudienceInsights',
  },
];

export const TABLEAU_PROD_URL = 'https://tableau.digitalremedy.com';
export const TABLEAU_DEV_URL = 'https://dev-tableau.digitalremedy.com';

const tableauBaseUrlProd = `${TABLEAU_PROD_URL}/#`;
const tableauBaseUrlDev = `${TABLEAU_DEV_URL}/#`;

const siteIdProd = '79826f01-4e01-4965-a996-b662d8be4f28';
const siteIdDev = 'e5a6f961-1bb5-4f47-a077-050c85df981f';
export const MNI_OVERVIEW_TAB_OPTIONS = [
  {
    id: 'overview',
    handler: 'tableauReportHandler',
    value: 'Overview',
    viewUrl: '/views/MNIGenericTemplate',
    viewSubUrl: '/Overview',
    dataUrlsProd: [
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/1_Overview-OverallMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/88c4c366-b6bb-40c4-8069-6bb9425ab571/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/2_Overview-DailyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/71c1ad84-2c1e-437f-915a-5f749e2418f3/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/3_Overview-WeeklyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/1c67ae82-7d27-4b23-960f-5252ea1b92c4/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/4_Overview-MonthlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/275c8a09-529a-432a-996b-27482c135950/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/5_Overview-QuarterlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/4fab2118-75cd-4996-926e-e768dc97e721/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/6_Overview-YearlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/b6537585-ae7d-40c5-8c03-528a019a7c7c/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/7_Overview-PacingInformation`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/d30c5437-e104-49ab-acff-8fa04b7be800/data`,
      },
    ],
    dataUrlsDev: [
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/1_Overview-OverallMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/2f44d089-851e-413b-ba8e-9f5eb6ea69eb/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/2_Overview-DailyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/b0072f49-4a40-49a5-a0e0-28ddce7c202f/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/3_Overview-WeeklyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/1cc3802c-3cdb-4c4e-a608-082f209484db/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/4_Overview-MonthlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/ef4cd104-7f8c-4ca5-be14-e114dd4a62a9/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/5_Overview-QuarterlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/8ab3c386-f6d2-421f-a81e-5704debc034b/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/6_Overview-YearlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/c4eb0e41-2111-418a-8988-4bb4b78efe12/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/7_Overview-PacingInformation`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/0fa1cc41-3af5-4f7c-9e54-6001f0b8096c/data`,
      },
    ],
    demoViewUrl: '/views/DemoMNIGenericTemplate',
    demoViewSubUrl: '/Overview',
    demoDataUrlsProd: [
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/1_Overview-OverallMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/965adfbb-60c4-410a-80e2-685c0baaa5a7/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/2_Overview-DailyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/e9d0b908-39a8-4467-85f4-329e515dc282/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/3_Overview-WeeklyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/f3bb1d66-7998-4497-9ce0-014a13cb270e/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/4_Overview-MonthlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/841d8853-d1c9-4564-8b87-b3fa46f7c8a2/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/5_Overview-QuarterlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/1491bfdc-929f-46fe-acac-6fab914898cc/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/6_Overview-YearlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/5f73e18c-bfd3-44f6-bc34-79aaee7a9fa9/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/7_Overview-PacingInformation`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/a71387f3-c336-4569-9a7c-d329f7290318/data`,
      },
    ],
    demoDataUrlsDev: [
      {
        templateUrl: `${tableauBaseUrlDev}/viewsDemoMNIGenericDataDownloadDashboard/1_Overview-OverallMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/cdf9a130-85a4-4a09-8f5b-5d5788883486/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/viewsDemoMNIGenericDataDownloadDashboard/2_Overview-DailyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/a1823eb6-fc33-4819-a783-a1829133109a/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/viewsDemoMNIGenericDataDownloadDashboard/3_Overview-WeeklyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/c1ca93ce-1c6a-496c-bf7f-c561ce1884e0/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/viewsDemoMNIGenericDataDownloadDashboard/4_Overview-MonthlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/805cc9f9-084a-4f38-b11d-5aa804ece874/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/viewsDemoMNIGenericDataDownloadDashboard/5_Overview-QuarterlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/179a44d4-c0d4-4cef-bc71-df708d59ef26/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/viewsDemoMNIGenericDataDownloadDashboard/6_Overview-YearlyMetrics`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/0a4de899-c8ce-444c-97d7-1d2138971d7b/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/viewsDemoMNIGenericDataDownloadDashboard/7_Overview-PacingInformation`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/f6b81d49-9030-4ae4-85b9-bee8106f5dc6/data`,
      },
    ],
    selected: true,
  },
  {
    id: 'distribution',
    handler: 'tableauReportHandler',
    value: 'Distribution',
    viewUrl: '/views/MNIGenericTemplate',
    viewSubUrl: '/Distribution',
    dataUrlsProd: [
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/8_Distribution-CreativeDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/7f5a9475-5ac4-42cd-ba32-e1ca7700f075/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/9_Distribution-DeviceDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/d396a1a0-1271-42c2-b50b-1b7d49ea6e7e/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/10_Distribution-DayPartDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/91556db4-a8d6-4d96-a658-13c719e541e1/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/11_Distribution-PlacementDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/28add357-1c62-40f5-a38e-d77f9ce2600a/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/12_Distribution-PublisherDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/6aeeb8cd-a881-4623-85ae-bc71539c38bb/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/13_Distribution-StateDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/83ae52de-1cc6-48c4-98f1-a3e7a6da0010/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/14_Distribution-DMADistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/24c195b8-e120-4d64-9977-1c46a1b0361f/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/15_Distribution-DaysToConvertDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/3153d49d-8bde-4730-84ec-09eb7e7396b0/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIGenericDataDownloadDashboard/16_Distribution-TimesExposedDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/f677b7e9-a0bc-4f7b-a321-85c3c4537935/data`,
      },
    ],
    dataUrlsDev: [
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/8_Distribution-CreativeDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/de6975c6-f32a-4284-a171-11eaa32ec78d/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/9_Distribution-DeviceDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/f74401f2-fc75-47d7-993c-39cfb4bfb4f1/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/10_Distribution-DayPartDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/de4dbab1-74cf-4c22-9d1c-81ce726d73aa/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/11_Distribution-PlacementDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/8c023a99-b421-4db9-8f02-41f142fa2853/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/12_Distribution-PublisherDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/d00069bd-4b28-4093-9d47-8ef89b3742d5/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/13_Distribution-StateDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/5a38c638-5778-4813-803c-efa4ed37b255/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/14_Distribution-DMADistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/8f0d5e4c-e904-4d87-90d5-6f1fa9c40593/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/15_Distribution-DaysToConvertDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/8e5fc6e8-82bb-42ef-afbd-196a508f46a3/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIGenericDataDownloadDashboard/16_Distribution-TimesExposedDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/66afd2a2-84ce-4031-b675-3f36b3d0efc8/data`,
      },
    ],

    demoViewUrl: '/views/DemoMNIGenericTemplate',
    // demoViewSubUrl: '/DistributionTemplate',
    demoViewSubUrl: '/Distribution',
    demoDataUrlsProd: [
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/8_Distribution-CreativeDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/37f7bdf2-5b06-414f-8cbb-c4d7b7204590/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/9_Distribution-DeviceDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/27c44eb5-730e-48f3-b56f-f26ecbd8bc88/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/10_Distribution-DayPartDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/5271d02e-c24d-4676-8a79-d19ef0520e28/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/11_Distribution-PlacementDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/3a5b6c27-cf40-422b-a123-47e059969aae/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/12_Distribution-PublisherDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/88c22a2a-b33c-4a1f-9724-3bbf49e26f45/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/13_Distribution-StateDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/f668b84f-ea73-4c2a-8393-ba19bdd01bd2/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/14_Distribution-DMADistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/840144a6-c348-4804-8571-eb1a6b745c3b/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/DemoMNIGenericDataDownloadDashboard/15_Distribution-DaysToConvertDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/f70c1a0a-6031-4122-8d8a-43b44dd940d7/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}views/DemoMNIGenericDataDownloadDashboard/16_Distribution-TimesExposedDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/c9f5b886-b522-4a38-af23-191bb0aff8e3/data`,
      },
    ],
    demoDataUrlsDev: [
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/7_Overview-PacingInformation`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/f6b81d49-9030-4ae4-85b9-bee8106f5dc6/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/8_Distribution-CreativeDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/bc226cb4-87e2-409c-8bdf-91ed9b27736f/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/9_Distribution-DeviceDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/623de5e7-4554-4a8f-b3f5-9805a838a88f/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/10_Distribution-DayPartDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/11f82a50-3f4f-4a37-8637-28f964543515/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/11_Distribution-PlacementDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/402bc2e1-7a33-44e0-bdb1-d540c90e41ac/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/12_Distribution-PublisherDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/fb6c7bf5-7723-43f5-951a-84eb0f452ba3/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/13_Distribution-StateDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/37c89127-1dcc-470f-b753-5335df48a2da/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/14_Distribution-DMADistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/5ae5125a-8cd6-4ccc-b0d2-18778cade388/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/15_Distribution-DaysToConvertDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/f5fc46f9-bbbd-4786-8264-6ab351f886df/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/DemoMNIGenericDataDownloadDashboard/16_Distribution-TimesExposedDistribution`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/f3fa5584-2c9c-488b-907d-86859676bc1d/data`,
      },
    ],
  },
  {
    id: 'journeyanalytics',
    handler: 'tableauReportHandler',
    value: 'Journey Analytics',
    viewUrl: '/views/MNIMottoMediaTypeJourneyDashboard/MediaTypeJourneyViz',
    demoViewUrl: '/views/MNIMottoMediaTypeJourneyDashboard/MediaTypeJourneyViz',
    dataUrlsProd: [
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIMottoMediaTypeJourneyDashboard/1_DataDownload-MediaTypeUserJourney`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/6d23cb5a-13e4-4d96-8ace-ce0ad6848760/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIMottoMediaTypeJourneyDashboard/2_DataDownload-PlacementUserJourney`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/33eb7497-96a9-4100-8a3e-18ed8abf44e6/data`,
      },
      {
        templateUrl: `${tableauBaseUrlProd}/views/MNIMottoMediaTypeJourneyDashboard/3_DataDownload-MediaTypeLift`,
        dataUrl: `/api/3.6/sites/${siteIdProd}/views/f3d2e3e3-b226-4363-bdc1-f45f2f610da9/data`,
      },
    ],
    dataUrlsDev: [
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIMottoMediaTypeJourneyDashboard/1_DataDownload-MediaTypeUserJourney`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/7fb348b9-c9f6-4063-afba-466ecea600b2/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIMottoMediaTypeJourneyDashboard/2_DataDownload-PlacementUserJourney`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/cd4a8625-53b7-438e-81ab-54e9696c329b/data`,
      },
      {
        templateUrl: `${tableauBaseUrlDev}/views/MNIMottoMediaTypeJourneyDashboard/3_DataDownload-MediaTypeLift`,
        dataUrl: `/api/3.6/sites/${siteIdDev}/views/f2a24a40-2a9a-426e-8a4a-792080da74c9/data`,
      },
    ],
    // Demo entries are not there. Once Demo ones are added,the following ones should be added.
    // demoDataUrlsProd: []
    // demoDataUrlsDev: []
  },
  {
    id: 'incrementality',
    handler: 'incrementalityHandler',
    value: 'Incrementality',
    component: 'someUIdashboard',
  },
];
export const VIA_MEDIA_CLIENT_NAME_FOR_API = 'VIA Media 2021';
export const EMAIL_VALIDATION_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const EMAIL_VALIDATION_MSG = 'Please enter valid email id';
export const TEMPLATE_ID_ENABLED_FOR_ACCOUNTS = [76, 311, 572];

export const DISTRIBUTION_DIMENSION_TITLES = {
  venueType: 'Venue Type',
};

export const METRIC_DISTRIBUTION_DIMENSION_TITLES = {
  venueType: 'Venue Type',
};

// labelConfig is a constants json for label values such as header and input lable names for account specific
// ** NOTE: always add default value before adding account specific values
export const labelConfig = {
  topFilterSection: {
    defaultLabel: {
      campaigns: 'Campaigns',
      adGroup: 'Ad Group',
      Audience: 'Audience',
      conversions: 'Conversions',
    },
    accountIdSpecificLabel: {
      73: {
        campaigns: 'Contract ID',
        adGroup: 'ConLine ID',
        Audience: 'Tier',
      },
    },
  },
  advFilterSection: {
    defaultLabel: {
      campaigns: 'Campaigns',
      adGroup: 'Ad Group',
      audianceFilter: 'Filter by Audience',
    },
    accountIdSpecificLabel: {
      73: {
        campaigns: 'Contract ID',
        adGroup: 'ConLine ID',
        audianceFilter: 'Filter by Tier',
      },
      76: {
        audianceFilter: 'Filter by Placements',
      },
    },
  },
};

// FROM BLIP
export const DEFAULT_CONVERSION_WINDOW_DISPLAY = 31;
export const DEFAULT_CONVERSION_WINDOW_UNIT = 'DAYS';
export const DEFAULT_METHODOLOGY = 'last_touch';
export const UNCATEGORIZED_EVENT_CATEGORY = 'default';
export const FLIP_SELECTED_MENU_PAGE = 'FLIP_SELECTED_MENU_PAGE';
export const FLIP_PARAM_CACHE_KEY = 'filterCacheId';
export const TAR_COUNTRY = 8;
export const TAR_STATE = 10;
export const TAR_DMA = 9;
export const TAR_CITY = 24;
export const TAR_ZIP_CODES = 12;
export const TAR_PUBLISHER_FLIP_OTT_TARGETING = 40;
export const DEFAULT_LOGO_URL = '/assets/flip-nondr-logo.png';
export const DEFAULT_LOGIN_IMG = '/assets/flip-login-sq.jpg';
// constants name for dropdown in chart comps
export const IMPRESSIONS = 'Impressions';
export const SPEND = 'Spend';
export const HOMEPAGE_VIEWS = 'Homepage Views';
export const CHECKOUTS = 'Checkouts';
export const LOC_VISITS = 'Location Visits';
export const REVENUE = 'Revenue';
export const ROAS = 'ROAS';
export const CPA = 'CPA';
export const VCR = 'VCR';
export const CTR = 'CTR';
export const VIDEO_COMPLETION = 'Video Completions';
export const INCREMENTAL_REACH = 'Incremental Reach';
export const CLICKS = 'Clicks';
export const VIEWS = 'views';
export const HOMEPAGE = 'Homepage';

export const KIND_VIEWS = 'views';
export const KIND_SPEND = 'spend';
export const KIND_CPM = 'cpm';
export const KIND_EVENT = 'event';
export const KIND_METRIC = 'metric';
export const KIND_REACH = 'reach';
export const USE_PG_API_FOR_REPORT_BUILDER = false;

// pre defined report builder 2.0 templates
export const NEW_REPORT = 'new';
export const ALL_TOUCH_REPORT = 'all_touch';
export const LAST_TOUCH_REPORT = 'last_touch';
export const FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT = 'foot_traffic';
export const FOOT_TRAFFIC_SUMMARY_REPORT = 'foot_traffic_summary';
export const CAT_SUBCAT_REPORT = 'cat_subcat_report';
export const AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT = 'audience_insights_campaigns';
export const AUDIENCE_INSIGHTS_CONVERSION_REPORT = 'audience_insights_conversion';
export const PRE_DEFINED_TEMPLATES = [
  ALL_TOUCH_REPORT,
  LAST_TOUCH_REPORT,
  FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT,
  FOOT_TRAFFIC_SUMMARY_REPORT,
  AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT,
  AUDIENCE_INSIGHTS_CONVERSION_REPORT,
  CAT_SUBCAT_REPORT,
];
export const PRE_DEFINED_TEMPLATES_WITH_NEW = [
  NEW_REPORT,
  ALL_TOUCH_REPORT,
  LAST_TOUCH_REPORT,
  FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT,
  FOOT_TRAFFIC_SUMMARY_REPORT,
  AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT,
  AUDIENCE_INSIGHTS_CONVERSION_REPORT,
  CAT_SUBCAT_REPORT,
];
export const PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS = [
  ALL_TOUCH_REPORT,
  LAST_TOUCH_REPORT,
  FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT,
];
export const PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS = [
  AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT,
  AUDIENCE_INSIGHTS_CONVERSION_REPORT,
  CAT_SUBCAT_REPORT,
  FOOT_TRAFFIC_SUMMARY_REPORT,
];
export const INSIGHTS_CMPGN_DELIV_PRESET_TEMPLATE_NAME = 'AUDINSCAMPAIGN_DELIVERY';
export const INSIGHTS_CONV_EVNT_PRESET_TEMPLATE_NAME = 'AUDINSCONVERSION_EVENTS';
export const CAT_SUBCAT_REPORT_TEMPLATE_NAME = 'CAT_SUBCAT_REPORT';
export const FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE_NAME = 'FOOT_TRAFFIC_SUMMARY_REPORT';
export const INSIGHTS_PRESET_TEMPLATE_NAMES = [
  INSIGHTS_CMPGN_DELIV_PRESET_TEMPLATE_NAME,
  INSIGHTS_CONV_EVNT_PRESET_TEMPLATE_NAME,
  CAT_SUBCAT_REPORT_TEMPLATE_NAME,
  FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE_NAME,
];
export const LOC_VISIT_EVENT = 'Location Visits';
export const FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT_TEMPLATE = {
  id: 'foot_traffic',
  name: 'Foot Traffic Attributed Visits Report',
  infoText: 'Pre-defined',
};
export const FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE = {
  id: 'foot_traffic_summary',
  name: 'Foot Traffic Summary Report',
  infoText: 'Pre-defined',
};
export const AUDIENCE_INSIGHTS_REPORT_TEMPLATE = [
  {
    id: 'audience_insights_campaigns',
    name: 'Audience Insights - Campaign Delivery',
    infoText: 'Pre-defined',
  },
  {
    id: 'audience_insights_conversion',
    name: 'Audience Insights - Conversion Events',
    infoText: 'Pre-defined',
  },
];

export const REPORT_BUILDER_2_EVENT_MAP = {
  lead: 'Leads',
  checkout: 'Checkouts',
  revenue: 'Revenue',
  pageVisit: 'Page Visits',
  completions: 'Video Completions',
  clicks: 'Clicks',
  locationVisit: 'Location Visits',
  activity: 'Activities',
};

export const RUN_REPORT_TIMESTAMPS_KEY = 'runReportTimestamps';
export const HALO_EFFECT_TIMESTAMPS_KEY = 'haloEffectTimestamps';
export const INCREMENTALITY_TIMESTAMPS_KEY = 'incrementalityTimestamps';
export const MAX_RUN_REPORT_COUNT_REACH_ERROR_MSG =
  'You’ve reached the report limit. Please try again in few minutes.';
export const MAX_RUN_COUNT_REACH_ERROR_MSG =
  'You’ve reached the run limit. Please try again in few minutes.';

export const ACCOUNTS_TO_SHOW = [476];

export const DAILY_VIEW_KIND_OPTIONS = [
  { id: KIND_VIEWS, name: 'Impressions' },
  { id: KIND_SPEND, name: 'Spend' },
  { id: KIND_EVENT, name: 'Event' },
  { id: KIND_METRIC, name: 'Metric' },
];
export const DAILY_VIEW_NEW_KIND_OPTIONS = [
  { id: KIND_REACH, name: 'Reach' },
  { id: KIND_VIEWS, name: 'Impressions' },
  { id: KIND_SPEND, name: 'Spend' },
  { id: KIND_CPM, name: 'CPM' },
  { id: KIND_EVENT, name: 'Event' },
  { id: KIND_METRIC, name: 'Metric' },
];
export const DISTRIBUTION_KIND_OPTIONS = [
  { id: KIND_VIEWS, name: 'Impressions' },
  { id: KIND_SPEND, name: 'Spend' },
  { id: KIND_EVENT, name: 'Event' },
];

export const DISTRIBUTION_GROUP_BY_TIMES_EXPOSED = { id: 'timesexposed', name: 'Times Exposed' };
export const DISTRIBUTION_GROUP_BY_ADDRESSES = { id: 'subcategories', name: 'Address' };

export const DISTRIBUTION_GROUP_BY_OPTIONS_VCR = [
  { id: 'mediatype', name: 'Media Type' },
  { id: 'creative', name: 'Creative' },
  { id: 'publisher', name: 'Publisher' },
  { id: 'channel', name: 'Channel' },
  { id: 'audience', name: 'Audience' },
  { id: 'adgroup', name: 'Ad Group' },
];

export const DISTRIBUTION_GROUP_BY_OPTIONS_CTR = [
  ...DISTRIBUTION_GROUP_BY_OPTIONS_VCR,
  { id: 'device', name: 'Device Type' },
  { id: 'state', name: 'State' },
  { id: 'dma', name: 'DMA' },
];

export const DISTRIBUTION_GROUP_BY_OPTIONS = [
  ...DISTRIBUTION_GROUP_BY_OPTIONS_CTR,
  { id: 'daypart', name: 'Daypart (EST)' },
];

export const REPORT_OPTIONS = [
  { id: 'overall', name: 'Overall', type: 'performance' },
  { id: 'mediatype', name: 'Media Type', type: 'performance' },
  { id: 'creative', name: 'Creative', type: 'performance' },
  { id: 'publisher', name: 'Publisher', type: 'performance' },
  { id: 'channel', name: 'Channel', type: 'performance' },
  { id: 'audience', name: 'Audience', type: 'performance' },
  { id: 'adgroup', name: 'Ad Group', type: 'performance' },
  { id: 'device', name: 'Device Type', type: 'performance' },
  { id: 'state', name: 'State', type: 'performance' },
  { id: 'dma', name: 'DMA', type: 'performance' },
  { id: 'zip', name: 'Zip Code', type: 'performance' },
  { id: 'day', name: 'Day', type: 'performance' },
  { id: 'daypart', name: 'Daypart (EST)', type: 'performance' },
];

export const ERROR_MESSAGE_FETCH_TEMPLATE = 'Unable to Fetch Templates';
export const ERROR_MESSAGE_DELIVERY_SELECTION = 'Please select an option under Delivery';
export const ERROR_MESSAGE_SCHEDULED_PRESET_REPORT =
  "This report is already scheduled for delivery. Select 'Export' only if you want to send it immediately.";
export const ERROR_MESSAGE_NAME_REQUIRED =
  'Please provide a valid report name (cannot be empty or spaces only)';
export const ERROR_MESSAGE_GATEWAY_TIMEOUT =
  'Report generation timed out due to large data volume. Please try again with a smaller date range or fewer filters/dimensions, or contact support for assistance.';

export const MESSAGES = {
  REPORT_EMAIL_SUCCESS: (email) =>
    `Report successfully queued for delivery to: ${email}.\nPlease note that large reports may take some time to appear in your inbox.`,
};

export const NEW_REPORT_OPTIONS = [
  { id: 'overall', name: 'Overall', type: 'performance' },
  { id: 'mediatype', name: 'Media Type', type: 'performance' },
  { id: 'creative', name: 'Creative', type: 'performance' },
  { id: 'publisher', name: 'Publisher', type: 'performance' },
  { id: 'channel', name: 'Channel', type: 'performance' },
  { id: 'audience', name: 'Audience', type: 'performance' },
  { id: 'adgroup', name: 'Ad Group', type: 'performance' },
  { id: 'device', name: 'Device Type', type: 'performance' },
  { id: 'state', name: 'State', type: 'performance' },
  { id: 'dma', name: 'DMA', type: 'performance' },
  { id: 'zip', name: 'Zip Code', type: 'performance' },
];

export const NEW_REPORT_BUILDER_METRIC_OPTIONS = [
  {
    key: 'impressions',
    name: 'Imp',
    value: 'Impressions',
    formatter: { type: 'number', format: '0,0' },
  },
  {
    key: 'spend',
    name: '($)Spend',
    value: 'Spend',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'cpm',
    name: '($)CPM',
    value: 'CPM',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'checkouts',
    name: 'Visits',
    value: 'Visits',
    width: '95px',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  // {
  //   key: 'conversionRate',
  //   name: '(%)Conversion Rate',
  //   value: 'Conversion Rate',
  //   formatter: { type: 'number', format: '0.00' },
  // },
  {
    key: 'lead',
    name: 'Leads',
    value: 'Leads',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'leadConversionRate',
    name: '(%)Lead Conversion Rate',
    value: 'Lead Conversion Rate',
    formatter: { type: 'number', format: '0.00' },
  },
  {
    key: 'leadTimeToConvert',
    name: 'Leads Time to Convert',
    value: 'Leads Time to Convert',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'leadTotalExposures',
    name: 'Leads Total Exposures',
    value: 'Leads Total Exposures',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'checkoutTimeToConvert',
    name: 'Checkouts Time to Convert',
    value: 'Checkouts Time to Convert',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'checkoutTotalExposures',
    name: 'Checkouts Total Exposures',
    value: 'Checkouts Total Exposures',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'checkout',
    name: 'Checkouts',
    value: 'Checkouts',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'checkoutConversionRate',
    name: '(%)Checkout Conversion Rate',
    value: 'Checkout Conversion Rate',
    formatter: { type: 'number', format: '0.00' },
  },
  {
    key: 'pageVisitTimeToConvert',
    name: 'Page Visits Time to Convert',
    value: 'Page Visits Time to Convert',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'locationVisitTimeToConvert',
    name: 'Location Visits Time to Convert',
    value: 'Location Visits Time to Convert',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'activityTimeToConvert',
    name: 'Activities Time to Convert',
    value: 'Activities Time to Convert',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'pageVisitTotalExposures',
    name: 'Page Visits Total Exposures',
    value: 'Page Visits Total Exposures',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'locationVisitTotalExposures',
    name: 'Location Visits Total Exposures',
    value: 'Location Visits Total Exposures',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'activityTotalExposures',
    name: 'Activities Total Exposures',
    value: 'Activities Total Exposures',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'pageVisit',
    name: 'Page Visits',
    value: 'Page Visits',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'locationVisit',
    name: 'Location Visits',
    value: 'Location Visits',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'activity',
    name: 'Activities',
    value: 'Activities',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'pageVisitConversionRate',
    name: '(%)Page Visits Conversion Rate',
    value: 'Page Visits Conversion Rate',
    formatter: { type: 'number', format: '0.00' },
  },
  {
    key: 'locationVisitConversionRate',
    name: '(%)Location Visits Conversion Rate',
    value: 'Location Visits Conversion Rate',
    formatter: { type: 'number', format: '0.00' },
  },
  {
    key: 'activityConversionRate',
    name: '(%)Activities Conversion Rate',
    value: 'Activities Conversion Rate',
    formatter: { type: 'number', format: '0.00' },
  },
  {
    key: USE_PG_API_FOR_REPORT_BUILDER ? 'videoCompletionRate' : 'vcr',
    name: '(%)Completion Rate',
    value: 'Completion Rate',
    width: '165px',
    formatter: { type: 'number', format: '0' },
  },
  {
    key: USE_PG_API_FOR_REPORT_BUILDER ? 'videoCompletions' : 'completions',
    name: 'Completions',
    value: 'Completions',
    width: '125px',
    formatter: { type: 'number', format: '0,0' },
  },
  {
    key: USE_PG_API_FOR_REPORT_BUILDER ? 'clickThroughRate' : 'ctr',
    name: '(%)CTR',
    value: 'CTR',
    formatter: { type: 'number', format: '0.00' },
  },
  {
    key: 'clicks',
    name: 'Clicks',
    value: 'Clicks',
    formatter: { type: 'number', format: '0,0' },
  },
  {
    key: 'metric',
    name: 'metric',
    value: 'metric',
    formatter: { type: 'number', format: '0' },
  },
  {
    key: 'incrementalReach',
    name: 'Inc. Reach',
    value: 'Inc. Reach',
    formatter: { type: 'number', format: '0' },
  },
  // {
  //   key: 'pm',
  //   name: 'Performance Multiplier',
  //   value: 'Performance Multiplier',
  //   formatter: { type: 'number', format: '0,0.[00]' },
  // },
  {
    key: 'leadPm',
    name: 'Leads Performance Multiplier',
    value: 'Leads Performance Multiplier',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'checkoutPm',
    name: 'Checkout Performance Multiplier',
    value: 'Checkout Performance Multiplier',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'pageVisitPm',
    name: 'Page Visits Performance Multiplier',
    value: 'Page Visits Performance Multiplier',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'locationVisitPm',
    name: 'Location Visits Performance Multiplier',
    value: 'Location Visits Performance Multiplier',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'activityPm',
    name: 'Activities Performance Multiplier',
    value: 'Activities Performance Multiplier',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'reach',
    name: 'Reach',
    value: 'Reach',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'frequency',
    name: 'Frequency',
    value: 'Frequency',
    formatter: { type: 'number', format: '0.[00]' },
  },
  {
    key: 'leadCpa',
    name: 'Lead CPA',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'checkoutCpa',
    name: 'Checkout CPA',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'pageVisitCpa',
    name: 'Page Visit CPA',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'locationVisitCpa',
    name: 'Location Visit CPA',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'activityCpa',
    name: 'Activities CPA',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'revenue',
    name: '($)Rev',
    value: 'Revenue',
    formatter: { type: 'number', format: '0,0.00' },
  },
  {
    key: 'roas',
    name: '(%)ROAS',
    value: 'ROAS',
    formatter: { type: 'number', format: '0' },
  },
  {
    key: 'day',
    name: 'Day',
    value: 'Day',
    formatter: { type: 'datetime', format: 'M/D/YYYY' },
  },
  {
    key: 'dayPart',
    name: 'Hour',
    value: 'Hour',
  },
];

export const COST_METRICS_KEYS_TO_HIDE = ['spend', 'roas', 'revenue', 'cpa'];

export const FOOT_TRAFFIC_HEADERS = [
  {
    key: 'ipAddress',
    name: 'Hashed IP Address',
    value: 'Hashed IP Address',
    width: '105px',
  },
  {
    key: 'storeName',
    name: 'Store Name',
    value: 'Store Name',
  },
  {
    key: 'storeAddress',
    name: 'Store Address',
    value: 'Store Address',
  },
  {
    key: 'visitTimestamp',
    name: 'Visit Timestamp',
    value: 'Visit Timestamp',
    width: '110px',
    formatter: { type: 'datetime', format: 'MM/DD/YYYY HH:mm' },
    stickyColumnAnchor: true,
  },
  {
    key: 'visitMonth',
    name: 'Visit Month',
    value: 'Visit Month',
  },
  {
    key: 'visitDay',
    name: 'Visit Day',
    value: 'Visit Day',
  },
  {
    key: 'visitHour',
    name: 'Visit Hour',
    value: 'Visit Hour',
  },
  {
    key: 'visitDate',
    name: 'Visit Date',
    value: 'Visit Date',
    width: '90px',
    formatter: { type: 'date', format: 'MM/DD/YYYY' },
    stickyColumnAnchor: true,
  },
  {
    key: 'visitWeekday',
    name: 'Visit Weekday',
    value: 'Visit Weekday',
  },
  {
    key: 'visitType',
    name: 'Visit Type',
    value: 'Visit Type',
  },
  {
    key: 'campaign',
    name: 'Campaign',
    value: 'Campaign',
    width: '105px',
  },
  {
    key: 'creative',
    name: 'Creative',
    value: 'Creative',
  },
  {
    key: 'publisher',
    name: 'Publisher',
    value: 'Publisher',
  },
  {
    key: 'channel',
    name: 'Channel',
    value: 'Channel',
  },
  {
    key: 'audienceTactic',
    name: 'Audience Tactic',
    value: 'Audience Tactic',
  },
  {
    key: 'audience',
    name: 'Audience',
    value: 'Audience',
  },
  {
    key: 'mediaType',
    name: 'Media Type',
    value: 'Media Type',
  },
  {
    key: 'region',
    name: 'Region',
    value: 'Region',
  },
  {
    key: 'dma',
    name: 'DMA',
    value: 'DMA',
  },
  {
    key: 'zipCode',
    name: 'Zip Code',
    value: 'Zip Code',
  },
  {
    key: 'device',
    name: 'Device',
    value: 'Device',
  },
  {
    key: 'impTimestamp',
    name: 'Impression Timestamp',
    value: 'Impression Timestamp',
    width: '110px',
    formatter: { type: 'datetime', format: 'MM/DD/YYYY HH:mm' },
    stickyColumnAnchor: true,
  },
  {
    key: 'impMonth',
    name: 'Impression Month',
    value: 'Impression Month',
  },
  {
    key: 'impDay',
    name: 'Impression Day',
    value: 'Impression Day',
  },
  {
    key: 'impHour',
    name: 'Impression Hour',
    value: 'Impression Hour',
  },
  {
    key: 'impDate',
    name: 'Impression Date',
    value: 'Impression Date',
    width: '90px',
    formatter: { type: 'date', format: 'MM/DD/YYYY' },
    stickyColumnAnchor: true,
  },
  {
    key: 'impWeekday',
    name: 'Impression Weekday',
    value: 'Impression Weekday',
  },
  {
    key: 'conversionType',
    name: 'Conversion Type',
    value: 'Conversion Type',
  },
  {
    key: 'hoursToConvert',
    name: 'Hours to Convert',
    value: 'Hours to Convert',
  },
  {
    key: 'timesExposed',
    name: 'Times Exposed',
    value: 'Times Exposed',
  },
  {
    key: 'visitCountPriorToConversion',
    name: 'Visit Count Prior to Conversion',
    value: 'Visit Count Prior to Conversion',
  },
];

export const REPORTS_NEW_BUILDER_METRIC_OPTIONS = {
  rollup: [
    {
      key: 'uniqueId',
      name: 'Unique ID',
      value: 'Unique ID',
    },
    {
      key: 'ip',
      name: 'Hashed IP Address',
      value: 'Hashed IP Address',
      width: '105px',
    },
    {
      key: 'revenue',
      name: '($)Rev',
      value: '($)Rev',
      formatter: { type: 'number', format: '0,0.00' },
    },
    {
      key: 'subCategory',
      name: 'Sub Category',
      value: 'Sub Category',
    },
    {
      key: 'pixelTimestamp',
      name: 'Conversion Timestamp',
      value: 'Conversion Timestamp',
      width: '110px',
      formatter: { type: 'datetime', format: 'MM/DD/YYYY HH:mm' },
      stickyColumnAnchor: true,
    },
    {
      key: 'creative',
      name: 'Creative',
      value: 'Creative',
      width: '260px',
    },
    {
      key: 'publisher',
      name: 'Publisher',
      value: 'Publisher',
      width: '140px',
    },
    {
      key: 'channel',
      name: 'Channel',
      value: 'Channel',
      width: '140px',
    },
    {
      key: 'audience',
      name: 'Audience/ Tactic',
      value: 'Audience/ Tactic',
      width: '110px', // 120
    },
    {
      key: 'mediaType',
      name: 'Media Type',
      value: 'Media Type',
      width: '100px',
    },
    {
      key: 'state',
      name: 'State',
      value: 'State',
      width: '50px', // 80
    },
    {
      key: 'dma',
      name: 'DMA',
      value: 'DMA',
      width: '105px', // 140
    },
    {
      key: 'deviceType',
      name: 'Device Type',
      value: 'Device Type',
      width: '100px',
    },
    {
      key: 'impressionTimestamp',
      name: 'Imp Timestamp',
      value: 'Imp Timestamp',
      width: '105px',
      formatter: { type: 'datetime', format: 'MM/DD/YYYY HH:mm' },
    },
    {
      key: 'timeToConvert',
      name: 'Time to Convert (Hrs)',
      value: 'Time to Convert',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: 'timesExposed',
      name: 'Times Exposed',
      value: 'Times Exposed',
      formatter: { type: 'number', format: '0' },
    },
  ],
  performance: [
    {
      key: 'ep',
      name: 'All Events and Performance',
      value: 'All Events and Performance',
      checked: false,
    },
    {
      key: 'impressions',
      name: 'Imp',
      value: 'Impressions',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: 'spend',
      name: '($)Spend',
      value: 'Spend',
      formatter: { type: 'number', format: '0,0.00' },
    },
    {
      key: 'cpm',
      name: '($)CPM',
      value: 'CPM',
      formatter: { type: 'number', format: '0,0.00' },
    },
    {
      key: 'checkouts',
      name: 'Visits',
      value: 'Visits',
      width: '95px',
      formatter: { type: 'number', format: '0,0.[00]' },
    },
    // {
    //   key: 'lead',
    //   name: 'Leads',
    //   value: 'Leads',
    //   formatter: { type: 'number', format: '0,0.[00]' },
    // },
    // {
    //   key: 'conversionRate',
    //   name: '(%)Conversion Rate',
    //   value: 'Conversion Rate',
    //   formatter: { type: 'number', format: '0.00' },
    // },
    {
      key: USE_PG_API_FOR_REPORT_BUILDER ? 'videoCompletionRate' : 'vcr',
      name: '(%)Completion Rate',
      value: 'Completion Rate',
      width: '165px',
      formatter: { type: 'number', format: '0' },
    },
    {
      key: USE_PG_API_FOR_REPORT_BUILDER ? 'videoCompletions' : 'completions',
      name: 'Completions',
      value: 'Completions',
      width: '125px',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: USE_PG_API_FOR_REPORT_BUILDER ? 'clickThroughRate' : 'ctr',
      name: '(%)CTR',
      value: 'CTR',
      formatter: { type: 'number', format: '0.00' },
    },
    {
      key: 'clicks',
      name: 'Clicks',
      value: 'Clicks',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: 'metric',
      name: 'metric',
      value: 'metric',
      formatter: { type: 'number', format: '0' },
    },
    {
      key: 'incrementalReach',
      name: 'Inc. Reach',
      value: 'Inc. Reach',
      formatter: { type: 'number', format: '0' },
    },
    // {
    //   key: 'pm',
    //   name: 'Performance Multiplier',
    //   value: 'Performance Multiplier',
    //   formatter: { type: 'number', format: '0,0.[00]' },
    // },
    {
      key: 'reach',
      name: 'Reach',
      value: 'Reach',
      formatter: { type: 'number', format: '0,0.[00]' },
    },
    {
      key: 'frequency',
      name: 'Frequency',
      value: 'Frequency',
      formatter: { type: 'number', format: '0.[00]' },
    },
  ],
};

export const REPORT_BUILDER_METRIC_OPTIONS = {
  rollup: [
    {
      key: 'uniqueId',
      name: 'Unique ID',
      value: 'Unique ID',
    },
    {
      key: 'ip',
      name: 'Hashed IP Address',
      value: 'Hashed IP Address',
      width: '105px',
    },
    {
      key: 'revenue',
      name: '($)Rev',
      value: '($)Rev',
      formatter: { type: 'number', format: '0,0.00' },
    },
    {
      key: 'subCategory',
      name: 'Sub Category',
      value: 'Sub Category',
    },
    {
      key: 'pixelTimestamp',
      name: 'Conversion Timestamp',
      value: 'Conversion Timestamp',
      width: '110px',
      formatter: { type: 'datetime', format: 'MM/DD/YYYY HH:mm' },
      stickyColumnAnchor: true,
    },
    {
      key: 'creative',
      name: 'Creative',
      value: 'Creative',
      width: '260px',
    },
    {
      key: 'publisher',
      name: 'Publisher',
      value: 'Publisher',
      width: '140px',
    },
    {
      key: 'channel',
      name: 'Channel',
      value: 'Channel',
      width: '140px',
    },
    {
      key: 'audience',
      name: 'Audience/ Tactic',
      value: 'Audience/ Tactic',
      width: '110px', // 120
    },
    {
      key: 'mediaType',
      name: 'Media Type',
      value: 'Media Type',
      width: '100px',
    },
    {
      key: 'state',
      name: 'State',
      value: 'State',
      width: '50px', // 80
    },
    {
      key: 'dma',
      name: 'DMA',
      value: 'DMA',
      width: '105px', // 140
    },
    {
      key: 'deviceType',
      name: 'Device Type',
      value: 'Device Type',
      width: '100px',
    },
    {
      key: 'impressionTimestamp',
      name: 'Imp Timestamp',
      value: 'Imp Timestamp',
      width: '105px',
      formatter: { type: 'datetime', format: 'MM/DD/YYYY HH:mm' },
    },
    {
      key: 'timeToConvert',
      name: 'Time to Convert (Hrs)',
      value: 'Time to Convert',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: 'timesExposed',
      name: 'Times Exposed',
      value: 'Times Exposed',
      formatter: { type: 'number', format: '0' },
    },
  ],
  performance: [
    {
      key: 'ep',
      name: 'All Events and Performance',
      value: 'All Events and Performance',
      checked: false,
    },
    {
      key: 'impressions',
      name: 'Imp',
      value: 'Impressions',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: 'spend',
      name: '($)Spend',
      value: 'Spend',
      formatter: { type: 'number', format: '0,0.00' },
    },
    {
      key: 'cpm',
      name: '($)CPM',
      value: 'CPM',
      formatter: { type: 'number', format: '0,0.00' },
    },
    {
      key: 'checkouts',
      name: 'Visits',
      value: 'Visits',
      width: '95px',
      formatter: { type: 'number', format: '0,0.[00]' },
    },
    {
      key: 'conversionRate',
      name: '(%)Conversion Rate',
      value: 'Conversion Rate',
      formatter: { type: 'number', format: '0.00' },
    },
    {
      key: USE_PG_API_FOR_REPORT_BUILDER ? 'videoCompletionRate' : 'vcr',
      name: '(%)Completion Rate',
      value: 'Completion Rate',
      width: '165px',
      formatter: { type: 'number', format: '0' },
    },
    {
      key: USE_PG_API_FOR_REPORT_BUILDER ? 'videoCompletions' : 'completions',
      name: 'Completions',
      value: 'Completions',
      width: '125px',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: USE_PG_API_FOR_REPORT_BUILDER ? 'clickThroughRate' : 'ctr',
      name: '(%)CTR',
      value: 'CTR',
      formatter: { type: 'number', format: '0.00' },
    },
    {
      key: 'clicks',
      name: 'Clicks',
      value: 'Clicks',
      formatter: { type: 'number', format: '0,0' },
    },
    {
      key: 'metric',
      name: 'metric',
      value: 'metric',
      formatter: { type: 'number', format: '0' },
    },
    {
      key: 'incrementalReach',
      name: 'Inc. Reach',
      value: 'Inc. Reach',
      formatter: { type: 'number', format: '0' },
    },
    {
      key: 'pm',
      name: 'Performance Multiplier',
      value: 'Performance Multiplier',
      formatter: { type: 'number', format: '0,0.[00]' },
    },
    {
      key: 'reach',
      name: 'Reach',
      value: 'Reach',
      formatter: { type: 'number', format: '0,0.[00]' },
    },
    {
      key: 'frequency',
      name: 'Frequency',
      value: 'Frequency',
      formatter: { type: 'number', format: '0.[00]' },
    },
  ],
};
export const REPORT_BUILDER_EXTRA_METRICS = [
  {
    key: 'timeToConvert',
    name: 'Time to Convert (Hrs)',
    value: 'Time to Convert',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
  {
    key: 'totalExposures',
    name: 'Total Exposures',
    value: 'Total Exposures',
    formatter: { type: 'number', format: '0,0.[00]' },
  },
];

export const REPORT_BUILDER_DISABLED_METRICS = {
  daypart: ['videoCompletionRate', 'videoCompletions', 'vcr', 'completions'],
  // device: ['videoCompletionRate', 'videoCompletions', 'vcr', 'completions'],
  state: ['videoCompletionRate', 'videoCompletions', 'vcr', 'completions'],
  // dma: ['videoCompletionRate', 'videoCompletions', 'vcr', 'completions'],
  day: ['videoCompletionRate', 'videoCompletions', 'vcr', 'completions'],
  zipcode: ['videoCompletionRate', 'videoCompletions', 'vcr', 'completions'],
};

export const INCREMENTALITY_GROUP_BY_OPTIONS = [
  { id: 'overall', name: 'Overall Lift' },
  { id: 'creative', name: 'Creative' },
  { id: 'publisher', name: 'Publisher' },
  { id: 'audience', name: 'Audience' },
  { id: 'mediatype', name: 'Media Type' },
  { id: 'adgroup', name: 'Ad Group' },
];

export const DATE_FORMAT_YYYY_MM_DD = 'YYYY-MM-DD';

export const WINDOW_TYPE_IMPRESSION = 'IMPRESSION';
export const WINDOW_TYPE_REACH = 'REACH';
export const WINDOW_TYPE_CONVERSION = 'CONVERSION';
export const WINDOW_TYPE_LIFT = 'LIFT';

export const ENUM_IS_WATCH_STATS = {
  IMPRESSION: false,
  REACH: false,
  CONVERSION: false,
};
// left menu window type
export const WINDOW_TYPE_SUMMARY = 'SUMMARY';
export const WINDOW_TYPE_CREATIVE = 'CREATIVE';
export const WINDOW_TYPE_PUBLISHER = 'PUBLISHER';
export const WINDOW_TYPE_AUDIENCE = 'AUDIENCE';
export const WINDOW_TYPE_CHANNEL = 'CHANNEL';
export const WINDOW_TYPE_MEDIA_TYPE = 'MEDIA TYPE';
export const WINDOW_TYPE_AD_GROUP = 'AD GROUP';

export const LEFT_PANE_WINDOW_TYPES = [
  WINDOW_TYPE_SUMMARY,
  WINDOW_TYPE_CREATIVE,
  WINDOW_TYPE_PUBLISHER,
  WINDOW_TYPE_AUDIENCE,
  WINDOW_TYPE_CHANNEL,
  WINDOW_TYPE_MEDIA_TYPE,
  WINDOW_TYPE_AD_GROUP,
];

// right menu window type
export const WINDOW_TYPE_DAILY_VIEW = 'DAILY_VIEW';
export const WINDOW_TYPE_DISTRIBUTION = 'DISTRIBUTION';
export const WINDOW_TYPE_INCREMENTALITY = 'INCREMENTALITY';
export const WINDOW_TYPE_HALO_EFFECT = 'HALO_EFFECT';
export const WINDOW_TYPE_BRAND_LIFT = 'BRAND_LIFT';

export const APP_ADREADY_MNI_ID = 3;

export const RIGHT_PANE_WINDOW_TYPES = [WINDOW_TYPE_DAILY_VIEW, WINDOW_TYPE_DISTRIBUTION];

export const USER_PROVIDER_OPTIONS = ['NONE', 'GOOGLE', 'LOCAL', 'DR_SSO'];

export const ROLE_ID_INTERNAL_SUB_ADMIN = 10;
export const ROLE_ID_INTERNAL_USER = 11;
export const ROLE_ID_EXTERNAL_USER = 13;
export const MNI_ADVANCED_PERFORMANCE_TITLE = 'Media Now Interactive';
export const REPORT_BUILDER_TITLE = 'Report Builder';

export const LAYOUT_TYPES = {
  DASHBOARD: 'DASHBOARD',
  BL_CAMPAIGN_SETUP: 'BL_CAMPAIGN_SETUP',
  FLIPPIXEL: 'FLIPPIXEL',
  FLIP_PIXEL_VERIFICATION: 'LIP_PIXEL_VERIFICATION',
  CAMPAIGNS: 'CAMPAIGNS',
  NEW_CAMPAIGNS: 'NEW_CAMPAIGNS',
  ADMIN: 'ADMIN',
  ACCOUNT_SETUP: 'ACCOUNT_SETUP',
  REPORT_BUILDER: 'REPORT_BUILDER',
};

export const LI_STATUS_COLOR_CODES = {
  '1': '#d6dadf',
  '2': '#9cc8e4',
  '3': '#4fd1cf',
  '4': '#4fd19b',
  '5': '#006987',
  '7': '#fdce07',
  '8': '#e6575d',
  '9': '#3d9afb',
  '10': '#fd9807',
  '23': '#efd1c4',
  '24': '#efe3c4',
};

export const NEW_CAMPAIGN_PAGES = {
  CAMPAIGN_INFO: 'campaign_info',
  BRAND_SAFETY: 'brand_safety',
  LUCID: 'lucid',
  AD_GROUP_INFO: 'ad_group_info',
  AD_GROUP_TARGETING: 'ad_group_targeting',
  AD_GROUP_ADS: 'ad_group_ads',
  REVIEW_CAMPAIGN: 'review_campaign',
};
// pagination constants
export const PAGINATION_DEFAULT_ITEM_PER_PAGE = 25;
export const PAGINATION_OPTIONS = [25, 50, 100, 200];
export const FLIP_CAMPAIGN_GOAL_CPA_METRIC = { id: 1, label: 'CPA' };
export const FLIP_CAMPAIGN_GOAL_ROAS_METRIC = { id: 2, label: 'ROAS' };
export const FLIP_CAMPAIGN_GOAL_VCR_METRIC = { id: 3, label: 'VCR' };
export const FLIP_CAMPAIGN_GOAL_REACH_METRIC = { id: 4, label: 'REACH' };

// pixel sub pages
export const FLIP_PIXEL_SUBPAGE = 'flip_pixel_subpage';
export const FLIP_PIXEL_SUBPAGE_CODE = 'code';
export const FLIP_PIXEL_SUBPAGE_VERIFY = 'verify';

export const FLIP_CAMPAIGN_GOAL_METRICS = [
  FLIP_CAMPAIGN_GOAL_CPA_METRIC,
  FLIP_CAMPAIGN_GOAL_ROAS_METRIC,
];

export const FLIP_CAMPAIGN_AWARENESS_METRICS = [
  FLIP_CAMPAIGN_GOAL_VCR_METRIC,
  FLIP_CAMPAIGN_GOAL_REACH_METRIC,
];
export const FLIP_CAMPAIGN_INFO_DEFAULT = {
  goal: {
    name: 'Awareness',
    category: 'vcr',
    isDisabled: false,
    isActive: true,
    subCategories: [],
    metric: FLIP_CAMPAIGN_GOAL_VCR_METRIC,
    defaultMetric: 'VCR',
  },
  methodology: {
    id: 1,
    value: 'last touch',
  },
  optimization: {
    weightage: {
      id: 111,
      value: 'direct',
    },
    attribution: {
      id: 1,
      value: 'Full Conversions',
    },
    impressions: {
      id: 11,
      value: 'total',
    },
  },
  conversionWindow: {
    unit: {
      id: 1,
      value: 'days',
    },
    value: 31,
  },
};
export const LUCID_CAMPAIGN_INFO_DEFAULT = {
  brandLiftInfo: {
    category: null,
    expectedImpressionCount: null,
    type: { id: 1, value: 'products' },
    competitors: [],
  },
};
export const CAMPAIGN_NAV_BAR_OPTIONS = [
  {
    title: 'Campaign Detail',
    leftIcon: 'star',
    rightIcon: 'check',
    pageName: NEW_CAMPAIGN_PAGES.CAMPAIGN_INFO,
    pageIndex: 1,
  },
  {
    title: 'Brand Safety & Verification',
    leftIcon: 'shield-check',
    rightIcon: 'check',
    pageName: NEW_CAMPAIGN_PAGES.BRAND_SAFETY,
    pageIndex: 2,
  },
  {
    title: 'Brand Lift Information',
    leftIcon: 'chart-line',
    rightIcon: 'check',
    pageName: NEW_CAMPAIGN_PAGES.LUCID,
    pageIndex: 3,
    disabled: true,
  },
];
export const ACC_SETUP_NAV_BAR_OPTIONS = [
  {
    id: 'ACCOUNT_INFO',
    title: 'Account Info',
    leftIcon: 'user',
    rightIcon: 'check',
    isActive: false,
    route: 'ACCOUNT_SETUP',
    elementId: 'acc-information-account-setup',
  },
  {
    id: 'PAYMENT',
    title: 'Payment',
    leftIcon: 'wallet',
    rightIcon: 'check',
    isActive: false,
    route: 'ACCOUNT_SETUP',
    elementId: 'acc-setup-payment-account-setup',
  },
  {
    id: 'ADD_USERS',
    title: 'Add Users',
    leftIcon: 'user-friends',
    rightIcon: 'check',
    isSolidLeftIcon: true,
    isActive: false,
    route: 'ACCOUNT_SETUP',
    elementId: 'acc-add-users-account-setup',
  },
  {
    id: 'PIXEL_SETUP',
    title: 'Pixel Setup',
    leftIcon: 'code',
    rightIcon: 'check',
    isActive: false,
    isDisabled: false,
    route: 'ACCOUNT_SETUP',
    elementId: 'acc-pixel-account-setup',
    subOptions: [
      {
        title: 'Pixel Code',
        route: 'PIXEL_SETUP_CODE',
        rightIcon: 'check',
        isActive: false,
      },
      {
        title: 'Pixel Installation Guide',
        route: 'PIXEL_SETUP_INSTALLATION',
        rightIcon: 'check',
        isActive: false,
      },
      {
        title: 'Pixel Verification',
        route: 'PIXEL_SETUP_VERIFICATION',
        rightIcon: 'check',
        isActive: false,
        isSolidLeftIcon: false,
      },
    ],
  },
];
export const AD_GROUP_NAV_BAR_OPTIONS = [
  {
    title: 'Ad Group Information',
    leftIcon: 'info-circle',
    rightIcon: 'check',
    pageName: NEW_CAMPAIGN_PAGES.AD_GROUP_INFO,
  },
  {
    title: 'Targeting',
    leftIcon: 'bullseye-arrow',
    rightIcon: 'check',
    pageName: NEW_CAMPAIGN_PAGES.AD_GROUP_TARGETING,
  },
  {
    title: 'Ads',
    leftIcon: 'ad',
    rightIcon: 'check',
    pageName: NEW_CAMPAIGN_PAGES.AD_GROUP_ADS,
  },
];
export const DELIVERY_PROBABILITY_OPTIONS = [
  {
    level: 'Low',
    text:
      'There is a low probability that this campaign will be able to deliver the expected impressions.',
    shortText: 'Unlikely to Deliver in Full',
    icon: 'tachometer-alt-slow',
    minScale: -99.9,
    maxScale: 20.0,
  },
  {
    level: 'Medium',
    text:
      'There is a medium probability that this campaign will be able to deliver the expected impressions.',
    shortText: 'Likely to Deliver in Full',
    icon: 'tachometer-alt-average',
    minScale: 20.01,
    maxScale: 30.0,
  },
  {
    level: 'High',
    text:
      'There is a high probability that this campaign will be able to deliver the expected impressions.',
    shortText: 'Very Likely to Deliver in Full',
    icon: 'tachometer-alt-fast',
    minScale: 30.01,
    maxScale: 99.9,
  },
];
export const CAMPAIGN_STATUS_FILTERS = {
  DRAFT: 1,
  NEW: 2,
  READY_FOR_ACTIVATION: 3,
  CAMPAIGN_LIVE: 5,
  ACTIVATED: 4,
  PAUSED: 7,
  CANCELED: 10,
  ENDED: 9,
};

export const DEFAULT_DEVICE_TYPES = [
  { deviceTypeId: 1 },
  { deviceTypeId: 2 },
  { deviceTypeId: 3 },
  { deviceTypeId: 5 },
];

export const DEFAULT_FREQUENCY_RECENCY = {
  impression: 3,
  recencyCapTimePeriod: 'Minutes',
  recencyCapValue: '30',
  timePeriod: 'Day',
};

export const DEFAULT_DAY_PARTING = {
  Mon: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  Tue: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  Wed: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  Thu: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  Fri: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  Sat: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  Sun: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
};

export const DAY_PARTING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAY_PARTING_HOURS = [
  { value: 0, text: '12', subText: 'AM' },
  { value: 1, text: '1' },
  { value: 2, text: '2' },
  { value: 3, text: '3' },
  { value: 4, text: '4' },
  { value: 5, text: '5' },
  { value: 6, text: '6' },
  { value: 7, text: '7' },
  { value: 8, text: '8' },
  { value: 9, text: '9' },
  { value: 10, text: '10' },
  { value: 11, text: '11' },
  { value: 12, text: '12', subText: 'PM' },
  { value: 13, text: '1' },
  { value: 14, text: '2' },
  { value: 15, text: '3' },
  { value: 16, text: '4' },
  { value: 17, text: '5' },
  { value: 18, text: '6' },
  { value: 19, text: '7' },
  { value: 20, text: '8' },
  { value: 21, text: '9' },
  { value: 22, text: '10' },
  { value: 23, text: '11' },
];
export const SELF_SERVE_OWNER_EMAIL = 'FlipplusAM@digitalremedy.com';
export const SELF_SERVE_OWNER_ID = 779;

export const CR_LINEITEM_STATUS = [LIS_CHANGE_NEW, LIS_CHANGE_PENDING_ACTIVATION];

export const ACCOUNT_UPDATE_EMAIL_RECIPIENTS = [
  'flipplusAM@digitalremedy.com',
  'ap@digitalremedy.com',
  'billing@digitalremedy.com',
];

export const PIXEL_INSTALLATION_GUIDE_URL =
  'https://docs.google.com/document/d/1OwNqRk6pTsm-RTPRebAGITfzV_JkJDndDBhLzBwyBkM/edit?usp=sharing';

export const THOUGHTSPOT_BASE_API_URL = 'https://digitalremedy.thoughtspot.cloud';

export const ACC_FQDN_VALIDATION_REGEX = /^([a-zA-Z0-9._-])+(\.co|\.com|\.io|\.uk|\.au|\.in|\.ca|\.nz|\.jp|\.bz|\.biz|\.net|\.org|\.gov)$/;
export const ACC_SUBDOMAIN_VALIDATION_REGEX = /^[a-zA-Z0-9_-]+$/;
export const ACC_NAME_VALIDATION_REGEX = /^[a-zA-Z0-9 _\-&]+$/;

export const APPLICATION_AI_ASSISTANT_REPORT_BUILDER = 9;
export const APPLICATION_AI_ASSISTANT_FULL_PLATFORM = 10;

export const OPEN_FILTER_OPTIONS = 'Open Filter Options';
export const CLOSE_FILTER_OPTIONS = 'Close Filter Options';

export const MAX_ADVANCED_FILTERS_STRING_LENGTH_MNI = 1200; // 60% of 2000 (best practice for max url lengths)

export const GENERIC_ERROR_MESSAGE =
  'An unexpected error occurred while processing your request. Please try again later.';
export const GENERIC_REPORT_GATEWAY_TIMEOUT_ERROR_MESSAGE =
  'Report may have timed out. Please try reloading the page, using the Email Report option, or contact support.';

export const CREATIVE_REPORT_KIND = 'creative';
export const DEFAULT_REPORT_KIND = CREATIVE_REPORT_KIND;

export const PRESET_REPORT_TEMPLATES_TOOLTIP_TEXT = [
  {
    name: 'all_touch',
    text: 'View the full journey to conversion across all media exposures.',
  },
  {
    name: 'last_touch',
    text: 'Identify the last media exposure that drove conversion.',
  },
  {
    name: 'foot_traffic',
    text: 'Analyze foot traffic conversions by location, visit timing, and last ad exposure.',
  },
  {
    name: 'foot_traffic_summary',
    text: 'Summary of foot traffic conversions by location, visit timing, and last ad exposure.',
  },
  {
    name: 'new',
    text: 'Create a custom report template from scratch with your chosen metrics.',
  },
  {
    name: 'audience_insights_campaigns',
    text:
      'Analyze the composition of exposed users and see how they compare to the U.S. population.',
  },
  {
    name: 'audience_insights_conversion',
    text: 'Profile exposed users who converted and see how they compare to the U.S. population.',
  },
  {
    name: 'cat_subcat_report',
    text: 'Break down of checkout conversions by subcategory.',
  },
];

export const FIRST_DAG_SUBMISSION_FAILURE_MESSAGE =
  'The report generation request couldn’t be triggered right now. The system will automatically retry in the background — no further action is required.';
export const PARTIAL_SUCCESS = 'partial_success';
export const MAX_SUB_CAT_INFO_TEXT =
  'Subcategory data capped at 50 for display. Deselect subcategory filter to view all data.';

export const FEATURE_CROSS_VIEW_NETWORKS = 'Cross View Networks';
export const FEATURE_CROSS_VIEW_PERFORMANCE = 'Cross View Performance';
export const FEATURE_CROSS_VIEW_AUDIENCE_DEMOGRAPHICS = 'Cross View Audience Demographics';

export const DEMO_CROSS_VIEW_DEFAULT_ADVERTISER_ID = 3997;
export const DEMO_CROSS_VIEW_DEFAULT_UNIVERSAL_PIXEL_ID = 'dec33815-0576-4b84-ade9-f43eb1c993f2';
export const DEMO_CROSS_VIEW_DEFAULT_XANDR_ID = '11875468';
